import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import qs from 'qs'
import { FormattedMessage } from 'react-intl'
import { useIntersectionObserver } from 'usehooks-ts'
import { clamp, throttle } from 'lodash'
import { Typography, Container, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  ProductComparateFragment,
  useProductComparisonQuery,
} from '@src/queries/ProductComparisonQuery.generated'
import DynamicProductCard from '@src/components/DynamicProductCarousel/DynamicProductCard'
import {
  CarouselProduct,
  CarouselProductTypes,
} from '@src/components/DynamicProductCarousel/DynamicProductCardTypes'
import { ProductAttribute } from '@src/types/graphql-types'
import HandleMarkup from '@src/components/HandleMarkup'
import { ProductCardType } from '@utils/searchUtils'
import { DynamicProductFragment } from '@src/fragments/DynamicProduct.generated'
import ProductQuickLook from '@src/components/ProductQuickLook/ProductQuickLook'
import {
  sendProductDetailsEvent,
  sendProductInfoInteractionEvent,
  sendEmptyRecommendationGA4Event,
} from '@src/utils/analytics'
import { sendCarouselPromoEvent } from '@src/utils/analytics/carouselAnalytics'
import {
  useCategorizePage,
  ProductCardTypeToPageMap,
} from '@utils/analytics/CategorizePage'
import { ProductDetailsEventAction } from '@utils/analytics/enums'
import { productDetailRoute } from '@src/routes'
import { buildProductCompareString } from '@src/utils/useCompareProducts'
import { Link } from '@src/components/Link'
import useResponsiveSizes from '@src/utils/useResponsiveSizes'
import { useProductCarouselControls } from '@src/components/DynamicProductCarousel/DynamicProductCarousel'
import { ProductCarouselControlProps } from '../DynamicProductCarousel/slideMovementHandler'
import Switch from '@src/components/Switch/SwitchIos'
import { ErpType } from '@utils/useProductErpType'
import { useCurrentUser } from '@utils/useCurrentUser'
import { SectionName } from '@src/components/ProductInfo/SkipToSectionTypes'

const NUM_COLUMNS = 4

const getGaLabel = (
  comparates: ProductComparateFragment[],
  interactedProduct: string | null
) => {
  const productNumbers = comparates.map((c) => c.product.productNumber)
  const prefix = interactedProduct ? `${interactedProduct} > ` : ''
  return `${prefix}${productNumbers.join('|')}`
}

/**
 * Small function to conform compare product data into some carousel analytics can use.
 * The product compare table is a modified carousel. Carousels supply their own analytics.
 * This extracts the data needed to trigger the analytics event.
 *
 * @param compareProductList
 * @returns
 */
const convertProductToCarouselProduct = (
  compareProductList: ProductComparateFragment[] | undefined | null
): CarouselProduct[] => {
  if (!compareProductList) {
    return []
  }

  return compareProductList.map((compItem) => {
    const product = compItem.product
    return {
      ...product,
      productKey: product.brand.key,
    }
  })
}

const useStyles = makeStyles((theme) => ({
  container: {
    overflow: 'hidden',
    position: 'relative', // prevents scrollable comparison table from making entire page horizontally scrollable
    paddingBottom: theme.spacing(20),
    [theme.breakpoints.up('sm')]: {
      paddingBottom: theme.spacing(10),
    },
  },
  scrollContainer: {
    scrollSnapType: 'x mandatory',
    overflowX: 'auto',
  },
  header: {
    padding: theme.spacing(10, 0, 4),
    display: 'flex',
    alignItems: 'center',
  },
  viewFullLink: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    marginLeft: theme.spacing(8),
    fontSize: theme.typography.pxToRem(16),
  },
  carouselContainer: {
    position: 'relative',
  },
  sideControl: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    '& + &': { right: -35 },
    [theme.breakpoints.up('md')]: { display: 'none' },
  },
  bottomControl: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },
  table: {
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
    borderSpacing: 0,
    '& th, & td': {
      verticalAlign: 'top',
      textAlign: 'left',
      [theme.breakpoints.up('sm')]: {
        borderRight: `1px solid ${theme.palette.grey[400]}`,
        '&:last-child': {
          borderColor: theme.palette.common.white,
        },
      },
      '&:first-child': {
        backgroundColor: theme.palette.background.grey,
      },
    },
    '& th': {
      padding: theme.spacing(1, 3, 0),
      scrollSnapAlign: 'start',
    },
    '& td': {
      padding: theme.spacing(0, 3, 2),
      position: 'relative',
    },

    '& tbody tr:last-child td': {
      paddingBottom: theme.spacing(3),
    },
  },
  label: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  value: {
    overflowWrap: 'break-word',
  },
  divider: {
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    marginBottom: theme.spacing(2),
  },
  highlight: {
    backgroundColor: theme.palette.background.highlight,
    '&::before': {
      content: "''",
      backgroundColor: theme.palette.common.white,
      position: 'absolute',
      bottom: 0,
      left: 0,
      top: 0,
      width: theme.spacing(3),
    },
    '&::after': {
      content: "''",
      backgroundColor: theme.palette.common.white,
      position: 'absolute',
      bottom: 0,
      right: 0,
      top: 0,
      width: theme.spacing(3),
    },
  },
  showDiffSwitch: {
    paddingLeft: theme.spacing(4),
  },
  verticalDivider: {
    height: theme.spacing(6),
    marginLeft: theme.spacing(6),
    marginRight: theme.spacing(4),
    width: theme.spacing(0.3),
    float: 'left',
    backgroundColor: theme.palette.grey[400],
  },
}))

interface ProductComparisonTableProps {
  comparates: ProductComparateFragment[]
  numColsVisible: number
  onViewPrice: (product: ProductComparateFragment['product']) => void
  showDifferences: boolean
}

export const ProductComparisonTable: React.FC<ProductComparisonTableProps> =
  memo(function ProductComparisonTable({
    comparates,
    numColsVisible,
    onViewPrice,
    showDifferences,
  }) {
    const classes = useStyles()
    const tableRef = useRef(null)
    const [tableSeen, setTableSeen] = useState(false)
    const comparisonTableViewed = useIntersectionObserver(tableRef, {
      threshold: 0.66,
    })
    const pageType = useCategorizePage()

    if (comparisonTableViewed?.isIntersecting && !tableSeen) {
      const creativeSlot = `${ProductCardType.CompareSimilarItems} - ${pageType}`

      sendCarouselPromoEvent({
        creativeSlot,
        pageType,
        products: convertProductToCarouselProduct(comparates),
      })

      setTableSeen(true)
    }

    const [base] = comparates || []
    if (!base) return null

    return (
      <table
        className={classes.table}
        style={{ width: `${(comparates.length / numColsVisible) * 100}%` }}
        role="table"
        aria-label="Comparison Table"
        ref={tableRef}
      >
        <colgroup>
          {comparates.map((_comparate, i) => (
            <col key={i} width={`${(1 / comparates.length) * 100}%`} />
          ))}
        </colgroup>
        <thead>
          <tr role="row">
            {comparates.map(({ product }, i) => (
              <th key={i} role="columnheader">
                {i === 0 ? (
                  <Typography variant="subtitle2">
                    <FormattedMessage
                      id="THIS_ITEM"
                      defaultMessage="This Item"
                    />
                  </Typography>
                ) : (
                  <Typography variant="srOnly">
                    {product.productNumber}
                  </Typography>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr role="row">
            {comparates.map(({ product }, i) => (
              <td key={i} role="cell">
                <DynamicProductCard
                  product={product}
                  testId={`compare-similar-${product.productNumber}`}
                  onViewPrice={() => onViewPrice(product)}
                  onLinkClick={() => {
                    sendProductDetailsEvent(
                      ProductDetailsEventAction.OpenItemPdp
                    )
                  }}
                  index={i}
                  type={CarouselProductTypes.CompareItems}
                />
              </td>
            ))}
          </tr>
        </tbody>
        <tbody>
          {base.comparisonAttributes.map(({ key, label }) => (
            <tr key={`${key}_${label}`} role="row">
              {comparates.map(
                (
                  { product, comparisonAttributes, attributeDifferences },
                  column
                ) => {
                  const isDifferent =
                    attributeDifferences && label && column !== 0
                      ? attributeDifferences.includes(label)
                      : false

                  const productAttribute = comparisonAttributes?.find(
                    (a) => a.key === key && a.label === label
                  )

                  const highlightClass =
                    isDifferent && showDifferences && column !== 0
                      ? classes.highlight
                      : ''

                  return (
                    <td
                      key={product.productNumber}
                      role="cell"
                      className={highlightClass}
                    >
                      <div className={classes.divider} />
                      <Typography variant="caption" className={classes.label}>
                        <HandleMarkup value={label} />
                      </Typography>
                      <mark>
                        <Typography
                          variant="body2"
                          className={`${classes.value}`}
                        >
                          {productAttribute ? (
                            <HandleMarkup value={productAttribute?.value} />
                          ) : (
                            '-'
                          )}
                        </Typography>
                      </mark>
                    </td>
                  )
                }
              )}
            </tr>
          ))}
        </tbody>
      </table>
    )
  })

export interface CompareSimilarItemsProps {
  productNumber: string
  properties: ProductAttribute[]
  brand: string
  productId: string
  setProductBadgeLabel?: Dispatch<SetStateAction<string>>
  productName: string | undefined
  updateSkipToLinks?: (sectionName: SectionName, ref: RefObject<null>) => void
}

export const CompareSimilarItems: React.FC<CompareSimilarItemsProps> = ({
  productNumber,
  brand,
  setProductBadgeLabel,
  updateSkipToLinks,
}) => {
  const ref = useRef(null)

  useEffect(() => {
    if (updateSkipToLinks && ref.current) {
      updateSkipToLinks('compareSimilar', ref)
    }
  }, [updateSkipToLinks])

  const { isBlueB2BUser } = useCurrentUser()
  const classes = useStyles()
  const getSize = useResponsiveSizes()
  const pageType = useCategorizePage()
  const numColsVisible = getSize({ xs: 1, sm: 2, md: 4 })
  const [quickAddProduct, setQuickAddProduct] =
    useState<DynamicProductFragment | null>(null)
  const [showDifferences, setShowDifferences] = useState(false)
  const { data } = useProductComparisonQuery({
    variables: {
      input: {
        productNumber,
        brand,
        count: NUM_COLUMNS - 1 /* `- 1` for the base product */,
        ...(isBlueB2BUser ? { erpType: ErpType.Blue } : {}),
      },
    },
    onCompleted: (loadedData) => {
      if (
        !loadedData ||
        loadedData?.getProductComparison?.comparates.length === 0
      ) {
        sendEmptyRecommendationGA4Event({
          action: 'pdp load',
          detail: productNumber.toLowerCase(),
          section: 'compare similar items',
          productId: productNumber.toLowerCase(),
          productBrand: brand.toLowerCase(),
        })
      }
    },
  })

  const comparates = data?.getProductComparison?.comparates

  useEffect(() => {
    if (setProductBadgeLabel && comparates) {
      setProductBadgeLabel(
        comparates[0]?.product?.tags ? comparates[0].product.tags : ''
      )
    }
  }, [comparates, setProductBadgeLabel])

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastManualAdvance = useRef(0)
  const [currentSlide, setCurrentSlide] = useState(0)
  const handleToggle = () => {
    setShowDifferences(!showDifferences)
    if (!showDifferences) {
      const eventLabel = comparates
        ?.map((item) => item.product.productNumber)
        .join('|')
      sendProductInfoInteractionEvent({
        action: 'highlight difference',
        detail: eventLabel,
        section: 'compare similar items',
        component: 'body',
        elementType: 'toggle',
        productId: productNumber,
        productBrand: brand,
      })
    }
  }

  const advanceSlide = (howMany = 1) => {
    lastManualAdvance.current = Date.now()
    if (!scrollContainerRef.current) return
    const maxIndex = (comparates || []).length - numColsVisible
    const nextIndex = clamp(currentSlide + howMany, 0, maxIndex)
    const { offsetWidth } = scrollContainerRef.current
    const left = (offsetWidth / numColsVisible) * nextIndex
    if (nextIndex !== currentSlide) {
      setCurrentSlide(nextIndex)
      scrollContainerRef.current.scrollTo({ left: left, behavior: 'smooth' })
    }
  }

  const onScroll = useMemo(() => {
    return throttle(
      () => {
        if (!scrollContainerRef.current) return

        const isManuallyAdvanced = Date.now() - lastManualAdvance.current <= 400
        if (isManuallyAdvanced) return

        const { offsetWidth, scrollLeft } = scrollContainerRef.current
        const columnWidth = offsetWidth / numColsVisible
        setCurrentSlide(Math.round(scrollLeft / columnWidth))
      },
      200,
      { leading: false }
    )
  }, [numColsVisible])

  const {
    renderCenterLeftControls,
    renderCenterRightControls,
    renderBottomCenterControls,
  } = useProductCarouselControls({
    products: convertProductToCarouselProduct(comparates),
    creativeSlot: `${ProductCardType.CompareSimilarItems} - ${pageType}`,
    pageType,
    pageTypeDetails: pageType,
  })

  const fullComparisonUrl = useMemo(() => {
    if (!comparates) return ''
    const compareQuery = {
      products: buildProductCompareString(
        comparates.map(({ product }) => ({
          brandKey: product.brand.key,
          productKey: product.productKey,
        }))
      ),
    }
    return `${productDetailRoute.compare()}?${qs.stringify(compareQuery)}`
  }, [comparates])

  if (!comparates || comparates?.length < 2) return null

  const carouselControlProps: ProductCarouselControlProps = {
    currentSlide,
    slidesToShow: numColsVisible,
    slideCount: comparates.length,
    previousSlide: () => advanceSlide(-1),
    nextSlide: () => advanceSlide(1),
    slidesToScroll: 1,
  }

  return (
    <div
      id="pdp-compare-similar--div"
      className="pdpGrid__section pdpGrid__section--white"
      ref={ref}
    >
      <Container maxWidth="lg" className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h2">
            <FormattedMessage
              id="COMPARE_SIMILAR_ITEMS"
              defaultMessage="Compare Similar Items"
            />
          </Typography>
          <Link href={fullComparisonUrl} passHref>
            {/* jsx-a11y rules disabled because href is implicitly passed to anchor via Link component */}
            {}
            <a
              className={classes.viewFullLink}
              onClick={() => {
                sendProductInfoInteractionEvent({
                  action: 'compare products',
                  detail: `${getGaLabel(comparates, null)}`,
                  section: 'compare similar items',
                  component: 'body',
                  elementType: 'link',
                  elementText: 'view full comparison',
                  linkUrl: fullComparisonUrl,
                  productId: productNumber,
                  productBrand: brand,
                })
              }}
            >
              <FormattedMessage
                id="VIEW_FULL_COMPARISON"
                defaultMessage="View Full Comparison"
              />
            </a>
          </Link>
          <Divider orientation="vertical" className={classes.verticalDivider} />
          <Switch
            labelOff={
              <Typography component="span">
                {' '}
                <FormattedMessage
                  id="SHOW_DIFFERENCES"
                  defaultMessage="Show Differences"
                />
              </Typography>
            }
            color="primary"
            onChange={handleToggle}
          />
        </div>
        <div className={classes.carouselContainer}>
          <div className={classes.sideControl}>
            {renderCenterLeftControls(carouselControlProps)}
          </div>
          <div className={classes.sideControl}>
            {renderCenterRightControls(carouselControlProps)}
          </div>
          <div className={classes.bottomControl}>
            {renderBottomCenterControls(carouselControlProps)}
          </div>
          <div
            ref={scrollContainerRef}
            className={classes.scrollContainer}
            onScroll={onScroll}
          >
            <ProductComparisonTable
              comparates={comparates}
              numColsVisible={numColsVisible}
              onViewPrice={setQuickAddProduct}
              showDifferences={showDifferences}
            />
          </div>
        </div>
      </Container>
      {quickAddProduct && (
        <ProductQuickLook
          {...quickAddProduct}
          image={quickAddProduct.images[0]}
          open
          onClose={() => setQuickAddProduct(null)}
          gaType={ProductCardTypeToPageMap[ProductCardType.CompareSimilarItems]}
          gaLabel={getGaLabel(comparates, quickAddProduct.productNumber)}
          showAddToList
          erpType={quickAddProduct?.erp_type || []}
        />
      )}
    </div>
  )
}
